import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

export type SystemStateType =
    | 'idle'
    | 'offline'
    | 'server_unavailable'
    | 'unexpected_error';

export type SystemState = {
    type: SystemStateType;
    message?: string;
};

const BLOCKING_OVERLAY_TYPES: ReadonlySet<SystemStateType> = new Set([
    'offline',
    'server_unavailable',
    'unexpected_error',
]);

@Injectable({
    providedIn: 'root',
})
export class SystemStateService {
    private readonly stateSubject = new BehaviorSubject<SystemState>({type: 'idle'});
    readonly state$ = this.stateSubject.asObservable();

    /**
     * Incremented on each router NavigationStart. HTTP handlers capture the
     * generation when a request starts and must not raise a blocking overlay
     * after a newer navigation has begun (stale Analytics → Dashboard race).
     */
    private navigationGeneration = 0;

    get current(): SystemState {
        return this.stateSubject.value;
    }

    getNavigationGeneration(): number {
        return this.navigationGeneration;
    }

    /**
     * Call from the app shell on NavigationStart (alongside BusyService.forceIdle).
     * Clears blocking overlays that would otherwise survive route changes.
     */
    notifyNavigationStart(): void {
        this.navigationGeneration += 1;
        if (BLOCKING_OVERLAY_TYPES.has(this.current.type)) {
            this.clear();
        }
    }

    /**
     * True when an HTTP failure still belongs to the active navigation epoch.
     */
    isCurrentNavigation(generation: number): boolean {
        return generation === this.navigationGeneration;
    }

    showOffline(message?: string): void {
        this.stateSubject.next({
            type: 'offline',
            message: message || 'Your internet connection appears to be offline.',
        });
    }

    showServerUnavailable(message?: string): void {
        this.stateSubject.next({
            type: 'server_unavailable',
            message: message || 'The Kyra server is temporarily unavailable. Please try again shortly.',
        });
    }

    showUnexpectedError(message?: string): void {
        this.stateSubject.next({
            type: 'unexpected_error',
            message: message || 'Something unexpected happened. Please refresh and try again.',
        });
    }

    clear(): void {
        this.stateSubject.next({type: 'idle'});
    }
}
